import { useState } from "react";
// Phase 23-08: useGetMember → useGetAccount (members table dropped)
import {
  useGetAccount, getGetAccountQueryKey,
  useListInvoices, getListInvoicesQueryKey,
  useListMemberCharges, getListMemberChargesQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { gstTreatmentLabel } from "@/lib/format";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { BillingStatusBadge, type BillingStatus } from "./billing-status-badge";
import { BillingSourceBadge } from "./billing-source-badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MarkPaidDialog } from "./mark-paid-dialog";
import { BookingsTab } from "@/components/members/bookings-tab";

type ChargeChip = "All" | "Pending" | "Disputed" | "Billed" | "Waived";

interface Props { memberId: number; }

export function MemberLedgerDrilldown({ memberId }: Props) {
  // Phase 23-08: useGetMember → useGetAccount (members table dropped)
  const { data: member } = useGetAccount(memberId, { query: { queryKey: getGetAccountQueryKey(memberId) } });
  // Phase 23-08: account does not directly carry planId or cadence; default to showing bookings tab
  const isNonRecurring = member?.kind === "walk_in";
  // Ledger shows the member's full invoice history — fetch all (high pageSize), not a single page.
  const invoiceParams = { memberId, pageSize: 200 };
  const { data: invoicePage } = useListInvoices(invoiceParams, { query: { queryKey: getListInvoicesQueryKey(invoiceParams) } });
  const invoices = invoicePage?.items ?? [];
  const [chargeChip, setChargeChip] = useState<ChargeChip>("All");
  const chargeParams = { memberId, pageSize: 200, ...(chargeChip === "All" ? {} : { status: chargeChip as "Pending" | "Disputed" | "Billed" | "Waived" }) };
  const { data: chargePage } = useListMemberCharges(chargeParams, { query: { queryKey: getListMemberChargesQueryKey(chargeParams) } });
  const charges = chargePage?.items ?? [];
  const [markPaidId, setMarkPaidId] = useState<number | null>(null);

  const outstanding = (invoices ?? []).reduce((s, i) => s + (Number(i.total) - Number(i.amountPaid)), 0);

  return (
    <div className="space-y-4">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink href="/billing">Billing</BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>Member Ledger</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>{member?.legalName ?? `ACC-${String(memberId).padStart(4, "0")}`}</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="grid grid-cols-[280px_1fr] gap-6">
        {/* Sidebar member summary card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <span className="font-mono text-xs text-muted-foreground">MEM-{String(memberId).padStart(4, "0")}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!member ? <Skeleton className="h-32 w-full" /> : (
              <>
                <div className="font-semibold text-lg">{member.legalName}</div>
                <div className="text-xs text-muted-foreground">Account type: {member.kind}</div>
                <div className="text-xs text-muted-foreground">Since: {new Date(member.createdAt).toLocaleDateString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">GST: {gstTreatmentLabel(member.gstTreatment)}</div>
                <div className="border-t pt-2 mt-2">
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Security Deposit</div>
                  <BillingStatusBadge status="Held" />
                  <div className="text-xs text-muted-foreground mt-1">(Lock-in until: see Deposit History tab)</div>
                </div>
                <div className={`border-t pt-2 mt-2 font-semibold ${outstanding > 0 ? "text-red-600" : "text-green-600"}`}>
                  {outstanding > 0
                    ? <>Outstanding balance: <span className="font-mono">Rs. {outstanding.toFixed(2)}</span></>
                    : "No outstanding balance"
                  }
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Right tabs */}
        <Tabs defaultValue="invoices">
          <TabsList>
            <TabsTrigger value="invoices">Invoice History</TabsTrigger>
            <TabsTrigger value="charges">Charges</TabsTrigger>
            {!isNonRecurring && (
              <TabsTrigger value="deposits">Deposit History</TabsTrigger>
            )}
            {isNonRecurring && (
              <TabsTrigger value="bookings">Bookings</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="invoices">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(invoices ?? []).map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-mono">{inv.invoiceNumber}</TableCell>
                      <TableCell className="text-xs">{inv.billingCycleStart} → {inv.billingCycleEnd}</TableCell>
                      <TableCell className="font-mono text-right">Rs. {Number(inv.total).toFixed(2)}</TableCell>
                      <TableCell><BillingStatusBadge status={inv.status as BillingStatus} /></TableCell>
                      <TableCell>
                        {inv.status !== "Paid" ? (
                          <Button size="sm" variant="outline" onClick={() => setMarkPaidId(inv.id)}>Mark Paid</Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(invoices ?? []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No invoices yet. This member's first invoice will appear after their billing day.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="charges">
            <div className="flex gap-2 mb-3">
              {(["All", "Pending", "Disputed", "Billed", "Waived"] as ChargeChip[]).map((c) => (
                <Button key={c} size="sm" variant={chargeChip === c ? "default" : "outline"} onClick={() => setChargeChip(c)}>{c}</Button>
              ))}
            </div>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(charges ?? []).map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-xs">{new Date(c.transactionDate).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell className="text-xs">{c.description}</TableCell>
                      <TableCell><BillingSourceBadge sourceType={c.sourceType} /></TableCell>
                      <TableCell className="font-mono text-right">Rs. {Number(c.total).toFixed(2)}</TableCell>
                      <TableCell><BillingStatusBadge status={c.status as BillingStatus} /></TableCell>
                    </TableRow>
                  ))}
                  {(charges ?? []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No charges yet. Pay-per-use and overage charges will appear here after Stock_Out events.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {!isNonRecurring && (
            <TabsContent value="deposits">
              <Card>
                <CardContent className="text-center text-muted-foreground py-12">
                  <div className="text-base font-semibold mb-1">No deposit history</div>
                  <div className="text-sm">No deposit history. Deposit recorded on first billing. Lock-in until: see member join date + 6 months. Endpoint added in follow-on phase.</div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
          {isNonRecurring && (
            <TabsContent value="bookings">
              <BookingsTab memberId={memberId} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Dialog open={markPaidId != null} onOpenChange={(o) => { if (!o) setMarkPaidId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Mark Paid</DialogTitle></DialogHeader>
          {markPaidId != null ? <MarkPaidDialog invoiceId={markPaidId} onClose={() => setMarkPaidId(null)} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
